/**
 * Derived field evaluation for computed columns.
 *
 * Uses math.js for expression evaluation with:
 * - Compile-once/evaluate-many pattern for performance
 * - Partial failure handling (continues on row errors)
 * - Infinity/NaN replacement with null
 *
 * Column access:
 * - Identifier-safe column names (alphanumeric, underscore, $) can be referenced
 *   directly: `price * quantity`.
 * - Columns whose names contain dots, colons, dashes, spaces, or any other
 *   character math.js would otherwise tokenize are accessed via the `row`
 *   wrapper using JavaScript-style bracket syntax:
 *   `row["string.externalId"]`, `row["metric:volume"]`, `row["kebab-name"]`.
 * - Mixing both styles in one expression is allowed:
 *   `price + row["tax.rate"] * quantity`.
 * - A column literally named `row` shadows the wrapper — if present, a warning
 *   is emitted and bracket-access syntax is unavailable for that dataset.
 *
 * Available functions — the authoritative, LLM-facing listing lives inline in
 * the `transformDataset` tool instructions (`TransformDatasetTool.tsx`, the
 * "Derive expressions" `<dl>` block). The math.js sandbox itself is wired up
 * in `math-config.ts` (math.import() calls register the custom string / date /
 * period helpers) and `timeseries-prefetch.ts` (timeseries fetchers + helpers,
 * registered per-row at evaluate time). math.js built-ins (sqrt(), abs(),
 * round(), mean(), sum(), min(), max(), count(), ...) are also available.
 */
import type { EvalFunction } from 'mathjs';

import { collectColumnRefsFromExpression } from './column-ref-extractor.ts';
import { math } from './math-config.ts';
import { type PrefetchCtx, prefetchAndBuildRegistry, TIMESERIES_FETCHER_NAMES } from './timeseries-prefetch.ts';
import type { DeriveResult, DeriveSpec } from './types.ts';
import { DerivedColumnAllNullError } from '#plugin-chat/lib/tool/errors/index.ts';

/** Compiled expression with metadata */
interface CompiledExpression {
  name: string;
  code: EvalFunction | null;
}

/** Default maximum errors to collect per field before summarizing */
const DEFAULT_MAX_ERRORS_PER_FIELD = 5;
/**
 * Below this row count, the all-null heuristic is too noisy — single-row
 * tests for legitimate Infinity/NaN handling, fixture-sized smoke tests,
 * and one-off sanity checks would all flag. Five rows is enough signal
 * that "every row null" reflects a real bug rather than tiny-input noise.
 */
const MIN_ROWS_FOR_ALL_NULL_CHECK = 5;
/**
 * Cap on per-row evaluation errors echoed into a {@link DerivedColumnAllNullError}.
 * Three samples is enough to show the agent the shape of the failure
 * (function name typo, type mismatch, etc.) without ballooning the error
 * message; the full collected error list is still available via
 * `details().sampleErrors` for tools that want to render more.
 */
const MAX_SAMPLE_EVAL_ERRORS = 3;

/**
 * Evaluate a single compiled derive expression against one row's scope.
 *
 * Extracted from {@link evaluateDerivedFields} so the main per-row loop
 * stays shallow — the try/catch and error-collection branches were pushing
 * the caller past the nesting-depth budget. Mutates `errorCounts` and
 * `errors` to thread collection state through the loop without passing
 * them back.
 */
function evaluateCompiledField(
  name: string,
  code: EvalFunction,
  scope: Record<string, unknown>,
  rowIndex: number,
  errorCounts: Record<string, number>,
  errors: DeriveResult['errors'],
  maxErrorsPerField: number,
): unknown {
  try {
    let value = code.evaluate(scope) as unknown;
    // Two — and only two — cleanups are applied before the value flows
    // into the dataset and into the chained-derive scope. Everything else
    // passes through verbatim so timeseries fetcher arrays, single
    // datapoints, Date timestamps, booleans from comparisons, etc. all
    // survive into downstream derives and downstream pipeline operators.
    //
    //  1. math.js wraps array literals (`[1,2,3]`) in a `Matrix`; the rest
    //     of the pipeline (Arquero, filter, unrollDatapoints, JSON
    //     serialization) expects JS arrays. Convert at the boundary.
    //  2. `Infinity` / `NaN` are valid JS numbers but break Arquero
    //     aggregations, sort, JSON, and display. Map to `null` — the
    //     canonical "no value" the rest of the pipeline already handles.
    if (math.isMatrix(value)) {
      value = (value as { toArray: () => unknown }).toArray();
    }
    if (typeof value === 'number' && !Number.isFinite(value)) {
      return null;
    }
    return value;
  } catch (e) {
    console.error(`Error evaluating derived field "${name}" on row ${rowIndex}:`, e);
    errorCounts[name] = (errorCounts[name] ?? 0) + 1;
    // Only collect detailed errors up to the limit — the summary warning
    // emitted by the caller reports the total count.
    if (errorCounts[name] <= maxErrorsPerField) {
      errors.push({
        row: rowIndex,
        field: name,
        error: e instanceof Error ? e.message : String(e),
      });
    }
    return null;
  }
}

/**
 * Evaluates derived fields on data rows using math.js expressions.
 *
 * Features:
 * - Expressions are compiled once, then evaluated per row (performance)
 * - Compilation failures are captured in warnings, field is skipped
 * - Row evaluation failures set field to null, collected in errors (up to maxErrorsPerField)
 * - Infinity/NaN results are replaced with null (prevents downstream issues)
 * - Fields with many errors get summary warnings
 * - Derives chain: each derive's result is written back into the per-row
 *   scope after evaluation, so a later derive can reference any earlier
 *   derive by bare name (e.g. `{name: 'doubled', expression: 'x * 2'}` then
 *   `{name: 'tripled', expression: 'doubled + x'}`). Forward references —
 *   a derive that names a later derive — fail to resolve and produce `null`
 *   with an error collected.
 * - Timeseries fetchers (`datapoints`, `hourly`, `daily`, `weekly`,
 *   `monthly`) are pre-fetched once per unique `(fnName, opts)` spec and
 *   exposed as scope-injected functions. Each fetcher call resolves
 *   synchronously against the prefetched lookup at row-evaluation time.
 *   Pass `ctx` with `client` + `queryClient` to enable the fetchers; omit
 *   it when no derive expression references a timeseries fetcher.
 * - All-null detection: after per-row evaluation, any derive whose output
 *   is null for every row of a non-trivial input (≥ MIN_ROWS_FOR_ALL_NULL_CHECK)
 *   AND collected zero per-row evaluation errors throws
 *   {@link DerivedColumnAllNullError}. Catches silent-undefined cascades —
 *   `row["foo.bar"]` against a row missing that flat key, fetchers that
 *   returned empty for every externalId, array reducers over empty arrays
 *   — without flagging legitimate errors-already-collected paths or
 *   tiny-input fixtures.
 *
 * @param data - Input data rows
 * @param derives - Derived field specifications
 * @param options - Optional ctx for timeseries prefetch and a per-field
 *   error-collection cap.
 * @returns Result with processed data, collected errors, and warnings
 */
export async function evaluateDerivedFields(
  data: Record<string, unknown>[],
  derives: DeriveSpec[],
  options?: { ctx?: PrefetchCtx; maxErrorsPerField?: number },
): Promise<DeriveResult> {
  const maxErrorsPerField = options?.maxErrorsPerField ?? DEFAULT_MAX_ERRORS_PER_FIELD;
  const errors: DeriveResult['errors'] = [];
  const warnings: string[] = [];

  // Compile all expressions upfront
  const compiled: CompiledExpression[] = derives.map((d) => {
    try {
      return { name: d.name, code: math.compile(d.expression) };
    } catch (e) {
      warnings.push(`Invalid expression for "${d.name}": ${e instanceof Error ? e.message : String(e)}`);
      return { name: d.name, code: null };
    }
  });

  // Filter to only valid compiled expressions
  const validCompiled = compiled.filter((c): c is CompiledExpression & { code: EvalFunction } => c.code !== null);

  // Return early if no valid expressions
  if (validCompiled.length === 0) {
    return { data, errors, warnings };
  }

  // Run the timeseries prefetch + registry build in parallel with scope
  // construction. When `ctx` is undefined or no derive references a
  // fetcher function, this is effectively a no-op that returns an empty
  // registry — the per-row scope still receives the registry shape so
  // fetcher calls in expressions return `[]` instead of throwing
  // "undefined symbol".
  const { registry: tsRegistry, warnings: prefetchWarnings } = await prefetchAndBuildRegistry({
    rows: data,
    derives,
    ctx: options?.ctx,
  });
  warnings.push(...prefetchWarnings);

  // Detect collisions with row-level identifiers introduced below. Two
  // collision classes share a single warning surface:
  //  - the `row` wrapper (used for bracket-access on awkwardly-named columns)
  //  - the timeseries fetcher names (`datapoints`, `hourly`, ...)
  // In both cases the explicit injection wins — a column named `row` or
  // `monthly` is shadowed by the wrapper / fetcher inside derive expressions.
  if (data.length > 0) {
    const sample = data[0] ?? {};
    if (Object.hasOwn(sample, 'row')) {
      warnings.push(
        'Column "row" shadows the derive expression wrapper. Bare `row` references in derive expressions resolve to the wrapper object, not the column value — rename the column via an earlier derive step to access it, or avoid using bracket-access syntax for awkwardly-named columns in this dataset.',
      );
    }
    for (const fnName of TIMESERIES_FETCHER_NAMES) {
      if (Object.hasOwn(sample, fnName)) {
        warnings.push(
          `Column "${fnName}" shadows the timeseries fetcher of the same name in derive expressions. Rename the column via an earlier derive step (or avoid the fetcher) to access it.`,
        );
      }
    }
  }

  // Track error counts per field
  const errorCounts: Record<string, number> = {};

  // Evaluate each row
  const result = data.map((row, rowIndex) => {
    const newRow = { ...row };

    // Math.js scope construction:
    // - Spread row values so bare identifier references (`price * tax`) keep
    //   working for identifier-safe column names.
    // - Add `row` as a wrapper so expressions can bracket-access columns
    //   whose names contain dots, colons, dashes, spaces, or any other
    //   character that math.js would otherwise tokenize (e.g.
    //   `row["string.externalId"]`, `row["metric:volume"]`).
    // - Spread the timeseries fetcher registry last so fetcher names
    //   (`datapoints`, `monthly`, ...) win over any shadowing column —
    //   collisions are warned about above.
    // Explicit annotation: TS narrows `{ ...row, row }` to `{ row: ... }`
    // (the literal property dominates after spread), losing the index
    // signature we need to write chained-derive results back via
    // `scope[name] = value` below.
    const scope: Record<string, unknown> = { ...row, row, ...(tsRegistry as Record<string, unknown>) };

    // Derives chain in declaration order: each result is written back into
    // `scope` after evaluation so a later derive can reference any earlier
    // derive by bare name. Forward references resolve to undefined symbols
    // in math.js and produce `null` via evaluateCompiledField's error path.
    for (const { name, code } of validCompiled) {
      const value = evaluateCompiledField(name, code, scope, rowIndex, errorCounts, errors, maxErrorsPerField);
      newRow[name] = value;
      scope[name] = value;
    }

    return newRow;
  });

  // Add summary warnings for fields with many errors
  for (const [name, count] of Object.entries(errorCounts)) {
    if (count > maxErrorsPerField) {
      warnings.push(`Field "${name}": ${count} rows failed (showing first ${maxErrorsPerField})`);
    }
  }

  // All-null detection. After per-row evaluation, scan each derive's
  // output: if every row is null, throw — the column is unusable
  // downstream and the agent needs a loud, structured signal. Two shapes
  // share the same error:
  //  - Silent-undefined (no per-row errors collected) — math.js evaluated
  //    cleanly per row but to undefined. The error explains the
  //    silent-failure shape.
  //  - All rows errored (per-row errors throughout) — every row threw.
  //    The previous "skip if errors collected" guard was wrong: per-row
  //    errors get demoted to a "N rows failed" warning that the tool
  //    layer drops, so the agent saw success-with-warning when reality
  //    was a useless column. Now we surface a sample of those error
  //    messages on the thrown error so the agent sees the underlying
  //    cause (e.g. "sumRunning is not a function").
  // Below MIN_ROWS_FOR_ALL_NULL_CHECK we skip — tiny inputs are too
  // noisy and the row-floor is documented above.
  if (result.length >= MIN_ROWS_FOR_ALL_NULL_CHECK) {
    for (const compiledDerive of validCompiled) {
      const allNull = result.every((row) => row[compiledDerive.name] == null);
      if (!allNull) {
        continue;
      }
      const expression = derives.find((d) => d.name === compiledDerive.name)?.expression ?? '';
      const refs = collectColumnRefsFromExpression(expression);
      const availableNames = data.length > 0 ? Object.keys(data[0] ?? {}).sort() : [];
      const sampleErrors = errors.filter((e) => e.field === compiledDerive.name).slice(0, MAX_SAMPLE_EVAL_ERRORS);
      throw new DerivedColumnAllNullError(
        compiledDerive.name,
        expression,
        result.length,
        availableNames,
        refs.map((r) => r.source),
        sampleErrors,
      );
    }
  }

  return { data: result, errors, warnings };
}
